import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type ReCAPTCHA from 'react-google-recaptcha';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getAuthConfig, resendVerification, signUp } from '../services/SignupService';
import { useAuth } from '../../../shared/contexts/auth/AuthContext';
import { LOGIN, PROJECTS_ADD } from '../../../shared/routes/path';
import { REACT_APP_CLIENT_ID, REACT_APP_IS_DEBUG } from '../../../shared/utils/config';
import useDataLayer, { ISignUpDataLayer } from '../../../shared/hooks/global/UseDataLayer';

/** Domyślny odstęp między ponowieniami maila weryfikacyjnego, gdy backend nie poda własnego. */
const DEFAULT_RESEND_COOLDOWN_SEC = 600;

/**
 * Rejestracja konta: budowa payloadu, reCAPTCHA, fingerprint i obsługa weryfikacji mailem. Wydzielone
 * z formularza, bo mamy dwa wejścia w rejestrację (zwykłe i dedykowane dla Brand24) i różnią się one
 * wyglądem oraz JEDNYM polem payloadu — logika musi zostać jedna, inaczej rozjedzie się przy
 * pierwszej zmianie.
 *
 * `signupOrigin` to jedyne, co odróżnia oba wejścia po stronie danych. Backend NIE bierze go wprost:
 * porównuje ze swoją listą znanych wejść (`resolveSignupOrigin`) i dopiero z niej wyprowadza
 * `register_source` oraz `client_type`. Nieznana wartość schodzi do zwykłej rejestracji.
 */
export const useSignupSubmit = (signupOrigin?: string) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { pushDataLayer } = useDataLayer();

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const formLoadedAt = useRef(Date.now());

  const [apiError, setApiError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getAuthConfig()
      .then((res) => {
        setEmailVerificationEnabled(res.data.emailVerification);
        if (res.data.fingerprintProtection) {
          void FingerprintJS.load()
            .then((fp) => fp.get())
            .then((result) => setFingerprint(result.visitorId));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown > 0]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || !registeredEmail) return;
    setResendSuccess(false);
    try {
      const response = await resendVerification(registeredEmail);
      const retryAfter = response?.data?.retryAfter || DEFAULT_RESEND_COOLDOWN_SEC;
      setResendSuccess(true);
      setResendCooldown(retryAfter);
      localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retryAfter;
      if (retryAfter) {
        setResendCooldown(retryAfter);
        localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
      }
    }
  }, [resendCooldown, registeredEmail]);

  const submit = useCallback(
    async (email: string, password: string, honeypot?: string) => {
      if (!recaptchaRef.current) {
        setApiError('Please turn on the reCAPTCHA');
        return;
      }

      setIsSubmitting(true);
      const token = !REACT_APP_IS_DEBUG ? await recaptchaRef.current?.executeAsync() : '';
      const userData: Record<string, string> = {
        firstName: '',
        lastName: '',
        username: email,
        password,
        language: i18n.language?.split?.('-')?.[0] || i18n.language || 'en',
        client_id: REACT_APP_CLIENT_ID,
        grant_type: 'password',
        browserData: JSON.stringify(navigator.userAgent),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recaptchaToken: token ?? '',
        fingerprint,
        contactReason: honeypot ?? '',
        formFillTime: String(Date.now() - formLoadedAt.current),
        ...(signupOrigin ? { signupOrigin } : {}),
      };

      const urlEncodedData = new URLSearchParams();
      Object.entries(userData).forEach(([key, value]) => urlEncodedData.append(key, value));

      try {
        const response = await signUp(urlEncodedData);

        if (response.data.requiresVerification) {
          setApiError(null);
          setRegisteredEmail(email);
          setVerificationSent(true);
          setResendCooldown(DEFAULT_RESEND_COOLDOWN_SEC);
          localStorage.setItem(
            'resendCooldownUntil',
            String(Date.now() + DEFAULT_RESEND_COOLDOWN_SEC * 1000)
          );
          return;
        }

        login(response.data);
        pushDataLayer<ISignUpDataLayer>({
          event: 'sign_up',
          email_hashed: email,
          account_type: 'TRIAL',
          user_id: response.data.user.id.toString(),
        });
        navigate(PROJECTS_ADD);
      } catch (err: any) {
        if (err.statusCode === 401) {
          navigate(LOGIN);
          return;
        }
        setApiError(err?.response?.data?.error ?? null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [fingerprint, i18n.language, login, navigate, pushDataLayer, signupOrigin]
  );

  return {
    recaptchaRef,
    submit,
    isSubmitting,
    apiError,
    clearApiError: () => setApiError(null),
    verificationSent,
    registeredEmail,
    resendCooldown,
    resendSuccess,
    handleResend,
    emailVerificationEnabled,
  };
};

export default useSignupSubmit;
